"use client";
import { cn } from "@/lib/utils";
import jsPDF from "jspdf";
import iranyekanTtf from "@/fonts/iranyekanTtf";
import autoTable from "jspdf-autotable";
import { IBaseLabelValue, IInquiryDetailsResultValues } from "@/interfaces";
import PdfIcon from "../ui/icons/pdf";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Logo } from "@/components";
import MainLogo from "@public/svg/logo-2.svg";
import SiteLogo from "@public/svg/logo.svg";
import Loader from "@/components/ui/loader";

export interface DownloadPdfRef {
  download: () => Promise<void>;
}

interface IProps {
  id?: string;
  resultContent?:
    | IBaseLabelValue[]
    | string
    | string[][]
    | IInquiryDetailsResultValues[];
  fileName: string;
  tableTitle?: string;
  inputData?: IBaseLabelValue;
  useHtmlToPdf?: boolean;
  htmlContentRef?: React.RefObject<HTMLDivElement | null>;
  customHtmlContent?: React.ReactNode;
  className?: string;
  title?: string;
  showIcon?: boolean;
  labelClass?: string;
  useStructuredHtmlPdf?: boolean;
  htmlCanvasScale?: number;
  htmlImageQuality?: number;
  htmlSplitPages?: boolean;
  htmlAvoidPageBreaks?: boolean;
}

type StructuredPdfSection = {
  title: string;
  pageLabel?: string;
  rows: string[][];
  code?: string;
  qrDataUrl?: string;
};

type PdfLogoAsset = string | { src?: string };

let cachedPdfLogoDataUrl: string | null | undefined;

const getPdfLogoDataUrl = async () => {
  if (cachedPdfLogoDataUrl !== undefined) return cachedPdfLogoDataUrl;

  try {
    const logoSrc =
      typeof (SiteLogo as PdfLogoAsset) === "string"
        ? (SiteLogo as string)
        : (SiteLogo as { src?: string }).src;

    if (!logoSrc) {
      cachedPdfLogoDataUrl = null;
      return cachedPdfLogoDataUrl;
    }

    const response = await fetch(logoSrc);
    const svgText = await response.text();
    const dataUrl = svgText.match(
      /xlink:href="(data:image\/png;base64,[^"]+)"/,
    )?.[1];

    cachedPdfLogoDataUrl = dataUrl ?? null;
    return cachedPdfLogoDataUrl;
  } catch {
    cachedPdfLogoDataUrl = null;
    return cachedPdfLogoDataUrl;
  }
};

const DownloadPdf = forwardRef<DownloadPdfRef, IProps>(
  (
    {
      id = "download-pdf",
      resultContent,
      fileName,
      tableTitle,
      inputData,
      useHtmlToPdf = false,
      htmlContentRef,
      customHtmlContent,
      className,
      title = "دانلود نتیجه",
      showIcon = true,
      labelClass,
      useStructuredHtmlPdf = true,
      htmlCanvasScale,
      htmlImageQuality = 0.9,
      htmlSplitPages = false,
      htmlAvoidPageBreaks = false,
    },
    ref,
  ) => {
    const defaultHtmlRef = useRef<HTMLDivElement>(null);
    const htmlRef = htmlContentRef || defaultHtmlRef;
    const [isLoading, setIsLoading] = useState(false);
    const pdfFileName = `${fileName}-${Date.now()}`;
    jsPDF.API.events.push([
      "addFonts",
      function (this: jsPDF) {
        this.addFileToVFS("IRANYekan.ttf", iranyekanTtf);
        this.addFont("IRANYekan.ttf", "Yekan", "normal");
      },
    ]);

    // Expose download function to parent components via ref
    useImperativeHandle(ref, () => ({
      download: handleDownloadPDF,
    }));

    const reactNodeToString = (node: React.ReactNode): string => {
      if (node === null || node === undefined) {
        return "";
      }
      if (typeof node === "string" || typeof node === "number") {
        return String(node);
      }
      if (typeof node === "boolean") {
        return node ? "true" : "false";
      }
      if (React.isValidElement(node)) {
        try {
          const markup = renderToStaticMarkup(node);
          return markup.replace(/<[^>]*>/g, "").trim();
        } catch (error) {
          console.warn("Failed to convert React element to string:", error);
          return "[React Component]";
        }
      }
      if (Array.isArray(node)) {
        return node.map(reactNodeToString).join(", ");
      }
      if (typeof node === "object") {
        return "[Object]";
      }
      return String(node);
    };

    // Detect if user is on mobile
    const isMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );
    };

    const handleDownloadPDF = async () => {
      let doc: jsPDF;

      if (useHtmlToPdf && htmlRef.current) {
        doc = await generateHtmlToPdf();
      } else {
        doc = generateTablePdf();
      }

      if (doc) {
        const mobile = isMobile();

        if (mobile) {
          // Mobile-specific download approach
          handleMobileDownload(doc);
        } else {
          // Desktop: Open preview in new window
          handleDesktopPreview(doc);
        }
      }
    };

    const handleMobileDownload = (doc: jsPDF) => {
      try {
        setIsLoading(true);
        // Create blob URL and trigger download
        const pdfBlob = doc.output("blob");
        const pdfUrl = URL.createObjectURL(pdfBlob);

        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = `${pdfFileName}.pdf`;

        // Make the link invisible and add to DOM
        link.style.display = "none";
        document.body.appendChild(link);

        // Trigger click
        link.click();

        // Clean up
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(pdfUrl);
        }, 100);

        // Optional: Show user feedback
        showDownloadNotification();
      } catch (error) {
        console.error("Mobile download failed:", error);
        // Fallback: Try direct save
        try {
          doc.save(`${pdfFileName}.pdf`);
        } catch (saveError) {
          console.error("Direct save also failed:", saveError);
          // Last resort: Open in new tab
          handleFallbackOpen(doc);
        }
      } finally {
        setIsLoading(false);
      }
    };

    const handleDesktopPreview = (doc: jsPDF) => {
      try {
        setIsLoading(true);
        const pdfBlob = doc.output("blob");
        const pdfUrl = URL.createObjectURL(pdfBlob);

        // Try to open in new window
        const newWindow = window.open("", "_blank");

        if (newWindow) {
          newWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${fileName}.pdf</title>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <style>
                body {
                  margin: 0;
                  padding: 0;
                  font-family: Arial, sans-serif;
                  display: flex;
                  flex-direction: column;
                  height: 100vh;
                }
                .toolbar {
                  background: #2c3e50;
                  color: white;
                  padding: 10px 20px;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }
                .toolbar h3 {
                  margin: 0;
                  font-size: 16px;
                }
                .download-btn {
                  background: #5a4bd2;
                  color: white;
                  border: none;
                  padding: 8px 20px;
                  border-radius: 5px;
                  cursor: pointer;
                  font-size: 14px;
                  transition: background 0.3s;
                }
                .download-btn:hover {
                  background: #4a3bc2;
                }
                iframe {
                  flex: 1;
                  border: none;
                  width: 100%;
                }
              </style>
            </head>
            <body>
              <div class="toolbar">
                <h3>${fileName}.pdf</h3>
                <button class="download-btn" onclick="downloadPDF()">دانلود PDF</button>
              </div>
              <iframe src="${pdfUrl}" type="application/pdf"></iframe>
              <script>
                function downloadPDF() {
                  const link = document.createElement('a');
                  link.href = '${pdfUrl}';
                  link.download = '${pdfFileName}.pdf';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
                
                window.addEventListener('beforeunload', function() {
                  URL.revokeObjectURL('${pdfUrl}');
                });
              </script>
            </body>
          </html>
        `);
          newWindow.document.close();
        } else {
          // Popup blocked, use direct download
          const link = document.createElement("a");
          link.href = pdfUrl;
          link.download = `${pdfFileName}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
        }
      } catch (error) {
        console.error("Desktop preview failed:", error);
        handleFallbackOpen(doc);
      } finally {
        setIsLoading(false);
      }
    };

    const handleFallbackOpen = (doc: jsPDF) => {
      try {
        // Convert to base64 and open in new tab
        const pdfData = doc.output("dataurlstring");
        const newWindow = window.open();

        if (newWindow) {
          newWindow.document.write(
            `<iframe width='100%' height='100%' src='${pdfData}'></iframe>`,
          );
        } else {
          // Last resort: just download
          doc.save(`${pdfFileName}.pdf`);
        }
      } catch (error) {
        console.error("Fallback open failed:", error);
        doc.save(`${pdfFileName}.pdf`);
      }
    };

    const showDownloadNotification = () => {
      // Optional: Show a toast notification
      // You can implement this based on your UI library
      console.info("PDF download started");
    };

    const normalizePdfText = (value: string | null | undefined) =>
      (value ?? "").replace(/\s+/g, " ").trim();

    const toPdfText = (doc: jsPDF, value: string | null | undefined) => {
      const normalized = normalizePdfText(value);

      return normalized;
    };

    const extractPdfTitleAndPageLabel = (value: string) => {
      const normalized = normalizePdfText(value);
      const pageMatch = normalized.match(
        /(?:\s*\/\s*)?(صفحه\s+[\d۰-۹٠-٩]+\s+از\s+[\d۰-۹٠-٩]+)(\s*[)\]]?)\s*$/,
      );

      if (!pageMatch) {
        return { title: normalized, pageLabel: undefined };
      }

      const titleWithoutPage = normalizePdfText(
        normalized
          .slice(0, pageMatch.index)
          .replace(/[\s/،,؛:-]+$/, "")
          .replace(/\(\s*$/, ""),
      );

      return {
        title:
          pageMatch[2]?.trim() === ")" && titleWithoutPage.startsWith("(")
            ? `${titleWithoutPage})`
            : titleWithoutPage,
        pageLabel: pageMatch[1],
      };
    };

    const extractStructuredPdfSections = (
      element: HTMLElement,
    ): StructuredPdfSection[] | null => {
      const pages = Array.from(
        element.querySelectorAll<HTMLElement>(".pdf-page"),
      );
      const targets = pages.length > 0 ? pages : [element];

      const sections = targets
        .map((target) => {
          const rawTitle =
            normalizePdfText(target.querySelector("h2")?.textContent) ||
            tableTitle ||
            fileName;
          const { title, pageLabel } = extractPdfTitleAndPageLabel(rawTitle);
          const rows = Array.from(target.querySelectorAll("tbody tr"))
            .map((row) =>
              Array.from(row.querySelectorAll("td"))
                .slice(0, 2)
                .map((cell) => normalizePdfText(cell.textContent)),
            )
            .filter((row) => row.length === 2 && row.some(Boolean));
          const code = normalizePdfText(
            target.querySelector(".text-en")?.textContent,
          );
          const qrCanvas = target.querySelector<HTMLCanvasElement>("canvas");
          const qrDataUrl = qrCanvas?.toDataURL("image/png");

          return {
            title,
            pageLabel,
            rows,
            code: code || undefined,
            qrDataUrl,
          };
        })
        .filter((section) => section.rows.length > 0);

      return sections.length > 0
        ? sections.map((section, index) => ({
            ...section,
            pageLabel: `صفحه ${index + 1} از ${sections.length}`,
          }))
        : null;
    };

    const getPdfGeneratedAtText = () =>
      new Intl.DateTimeFormat("fa-IR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date());

    const drawStructuredPdfHeader = (
      doc: jsPDF,
      titleText: string,
      pageWidth: number,
      logoDataUrl: string | null,
    ) => {
      doc.setFillColor(90, 75, 210);
      doc.rect(0, 0, pageWidth, 34, "F");

      if (logoDataUrl) {
        doc.addImage(logoDataUrl, "PNG", pageWidth - 30, 8, 18, 18);
      }

      doc.setTextColor(255, 255, 255);
      doc.setFont("Yekan");
      doc.setFontSize(12.5);
      doc.text(toPdfText(doc, "سامانه استعلام نوین وان"), pageWidth - 34, 14, {
        align: "right",
      });

      doc.setFontSize(8.8);
      const titleLines = doc.splitTextToSize(toPdfText(doc, titleText), 142);
      doc.text(titleLines.slice(0, 1), pageWidth - 34, 24, {
        align: "right",
      });

      doc.setFontSize(7.5);
      doc.setTextColor(255, 255, 255);
      doc.text(toPdfText(doc, "تاریخ و ساعت گزارش"), 12, 14, {
        align: "left",
      });
      doc.setFontSize(8.2);
      doc.text(getPdfGeneratedAtText(), 12, 22, {
        align: "left",
      });

      doc.setTextColor(40, 40, 40);
    };

    const drawStructuredPdfFooter = (
      doc: jsPDF,
      section: StructuredPdfSection,
      pageWidth: number,
      pageHeight: number,
    ) => {
      const footerTop = pageHeight - 34;
      const qrX = 8;
      const qrSize = 18;
      const authenticityX = qrX + qrSize + 13;
      const footerTextRight = pageWidth - 12;
      const footerTextMaxWidth = pageWidth - 78;

      doc.setDrawColor(220, 220, 232);
      doc.line(12, footerTop, pageWidth - 12, footerTop);

      doc.setFont("Yekan");
      doc.setFontSize(8);
      doc.setTextColor(80, 80, 90);
      const firstFooterLine = doc.splitTextToSize(
        toPdfText(
          doc,
          "این گزارش توسط سامانه استعلامات نوین وان تولید شده است",
        ),
        footerTextMaxWidth,
      );
      const secondFooterLine = doc.splitTextToSize(
        toPdfText(
          doc,
          "اعتبار این استعلام را می‌توانید در بخش تأیید اصالت گزارش سایت بررسی کنید",
        ),
        footerTextMaxWidth,
      );
      doc.text(firstFooterLine, footerTextRight, footerTop + 8, {
        align: "right",
      });
      doc.text(secondFooterLine, footerTextRight, footerTop + 15, {
        align: "right",
      });

      if (section.code) {
        doc.setFontSize(7);
        doc.setTextColor(140, 140, 150);
        doc.text(
          toPdfText(doc, "شناسه اصالت گزارش"),
          authenticityX,
          footerTop + 8,
          {
            align: "center",
          },
        );
        doc.setFontSize(15);
        doc.setTextColor(211, 47, 47);
        doc.text(section.code, authenticityX, footerTop + 17, {
          align: "center",
        });
      }

      if (section.qrDataUrl) {
        doc.addImage(
          section.qrDataUrl,
          "PNG",
          qrX,
          footerTop + 4,
          qrSize,
          qrSize,
        );
      }

      if (section.pageLabel) {
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 135);
        doc.text(
          toPdfText(doc, section.pageLabel),
          pageWidth / 2,
          footerTop + 28,
          {
            align: "center",
          },
        );
      }

      doc.setTextColor(40, 40, 40);
    };

    const generateStructuredPdfFromHtml = async (
      element: HTMLElement,
    ): Promise<jsPDF | null> => {
      const sections = extractStructuredPdfSections(element);
      if (!sections) return null;
      const logoDataUrl = await getPdfLogoDataUrl();

      const doc = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      doc.setFont("Yekan");
      doc.setR2L?.(false);

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      sections.forEach((section, sectionIndex) => {
        if (sectionIndex > 0) {
          doc.addPage();
        }

        autoTable(doc, {
          head: [[toPdfText(doc, "نتیجه"), toPdfText(doc, "عنوان")]],
          body: section.rows.map((row) => {
            const [label, ...values] = row;
            return [toPdfText(doc, values.join(" ")), toPdfText(doc, label)];
          }),
          margin: { top: 48, right: 12, bottom: 42, left: 12 },
          tableWidth: "auto",
          theme: "grid",
          rowPageBreak: "avoid",
          pageBreak: "auto",
          styles: {
            font: "Yekan",
            fontSize: 9.5,
            cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
            halign: "right",
            valign: "middle",
            overflow: "linebreak",
            lineColor: [225, 225, 235],
            lineWidth: 0.15,
            textColor: [34, 34, 45],
          },
          headStyles: {
            font: "Yekan",
            fillColor: [90, 75, 210],
            textColor: [255, 255, 255],
            fontStyle: "normal",
            fontSize: 10.5,
            halign: "center",
          },
          bodyStyles: {
            fillColor: [255, 255, 255],
          },
          alternateRowStyles: {
            fillColor: [248, 247, 255],
          },
          columnStyles: {
            0: { cellWidth: "auto", textColor: [25, 25, 35] },
            1: { cellWidth: 52, fontStyle: "normal", textColor: [70, 65, 95] },
          },
          didDrawPage: () => {
            drawStructuredPdfHeader(doc, section.title, pageWidth, logoDataUrl);
            drawStructuredPdfFooter(doc, section, pageWidth, pageHeight);
          },
        });
      });

      return doc;
    };

    const waitForPdfAssets = async () => {
      if ("fonts" in document) {
        await document.fonts.ready;
      }

      await Promise.all(
        Array.from(htmlRef.current?.querySelectorAll("img") ?? []).map(
          (image) =>
            image.complete
              ? Promise.resolve()
              : new Promise<void>((resolve) => {
                  image.onload = () => resolve();
                  image.onerror = () => resolve();
                }),
        ),
      );

      await new Promise((resolve) => requestAnimationFrame(resolve));
    };

    const withCaptureReadyElement = async <T,>(
      element: HTMLElement,
      callback: () => Promise<T>,
    ): Promise<T> => {
      const previousStyles = {
        position: element.style.position,
        left: element.style.left,
        top: element.style.top,
        right: element.style.right,
        bottom: element.style.bottom,
        zIndex: element.style.zIndex,
        width: element.style.width,
        maxWidth: element.style.maxWidth,
        height: element.style.height,
        overflow: element.style.overflow,
        transform: element.style.transform,
        visibility: element.style.visibility,
        display: element.style.display,
        pointerEvents: element.style.pointerEvents,
      };

      element.style.position = "absolute";
      element.style.left = "0";
      element.style.top = "0";
      element.style.right = "auto";
      element.style.bottom = "auto";
      element.style.zIndex = "-10000";
      element.style.width = `${Math.max(element.scrollWidth, 800)}px`;
      element.style.maxWidth = "none";
      element.style.height = "auto";
      element.style.overflow = "visible";
      element.style.transform = "none";
      element.style.visibility = "visible";
      element.style.display = "block";
      element.style.pointerEvents = "none";

      try {
        await waitForPdfAssets();
        return await callback();
      } finally {
        Object.assign(element.style, previousStyles);
      }
    };

    const generateHtmlToPdf = async (): Promise<jsPDF> => {
      try {
        setIsLoading(true);

        const element = htmlRef.current;
        if (!element) throw new Error("No HTML element found");

        if (useStructuredHtmlPdf) {
          const structuredPdf = await generateStructuredPdfFromHtml(element);
          if (structuredPdf) {
            return structuredPdf;
          }
        }

        const html2canvas = (await import("html2canvas")).default;

        const pdf = new jsPDF({
          orientation: "p",
          unit: "mm",
          format: "a4",
          compress: false,
        });

        await withCaptureReadyElement(element, async () => {
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const pixelRatio =
            typeof window === "undefined" ? 2 : window.devicePixelRatio || 1;
          const mobileDevice = isMobile();
          const defaultCanvasScale = mobileDevice
            ? Math.min(Math.max(pixelRatio, 1.35), 1.6)
            : Math.min(Math.max(pixelRatio, 1.75), 2);
          const canvasScale = htmlCanvasScale ?? defaultCanvasScale;
          const pdfPages = Array.from(
            element.querySelectorAll<HTMLElement>(".pdf-page"),
          );
          const targets = pdfPages.length > 0 ? pdfPages : [element];

          for (const [index, target] of targets.entries()) {
            if (index > 0) {
              pdf.addPage();
            }

            let protectedBlocks: { top: number; bottom: number }[] = [];
            const canvas = await html2canvas(target, {
              useCORS: true,
              allowTaint: true,
              background: "#ffffff",
              scale: canvasScale,
              width: target.scrollWidth,
              height: target.scrollHeight,
              windowWidth: Math.max(target.scrollWidth, 800),
              windowHeight: Math.max(target.scrollHeight, 1100),
              scrollX: 0,
              scrollY: 0,
              logging: false,
              onclone: (
                _clonedDocument: Document,
                clonedTarget: HTMLElement,
              ) => {
                if (!htmlAvoidPageBreaks) return;

                const rootTop = clonedTarget.getBoundingClientRect().top;
                const firstRowAfter = (section: HTMLElement) =>
                  section.querySelector<HTMLElement>(
                    "tbody tr, [role='row'], p, li, div",
                  );

                protectedBlocks = Array.from(
                  clonedTarget.querySelectorAll<HTMLElement>("section, table"),
                ).flatMap((block) => {
                  const heading = block.querySelector<HTMLElement>("h1, h2, h3, h4, thead");
                  const firstRow = firstRowAfter(block);
                  if (!heading || !firstRow) return [];

                  const headingRect = heading.getBoundingClientRect();
                  const rowRect = firstRow.getBoundingClientRect();
                  return [
                    {
                      top: headingRect.top - rootTop,
                      bottom: Math.max(headingRect.bottom, rowRect.bottom) - rootTop,
                    },
                  ];
                });
              },
            } as Parameters<typeof html2canvas>[1]);

            if (htmlSplitPages) {
              const pixelsPerMm = canvas.width / pageWidth;
              const pageCanvasHeight = Math.floor(pageHeight * pixelsPerMm);
              const canvasPixelsPerCssPixel = canvas.width / target.scrollWidth;
              const protectedCanvasBlocks = protectedBlocks.map((block) => ({
                top: Math.floor(block.top * canvasPixelsPerCssPixel),
                bottom: Math.ceil(block.bottom * canvasPixelsPerCssPixel),
              }));
              let offset = 0;

              while (offset < canvas.height) {
                if (index > 0 || offset > 0) {
                  pdf.addPage();
                }

                let sliceHeight = Math.min(
                  pageCanvasHeight,
                  canvas.height - offset,
                );

                if (htmlAvoidPageBreaks && offset + sliceHeight < canvas.height) {
                  const splitAt = offset + sliceHeight;
                  const overlappingBlock = protectedCanvasBlocks.find(
                    (block) => block.top < splitAt && block.bottom > splitAt,
                  );
                  const safeHeight = overlappingBlock
                    ? overlappingBlock.top - offset
                    : 0;

                  // Move a section title and its first row together to the next page.
                  if (safeHeight > pageCanvasHeight * 0.18) {
                    sliceHeight = safeHeight;
                  }
                }
                const slice = document.createElement("canvas");
                slice.width = canvas.width;
                slice.height = sliceHeight;
                const context = slice.getContext("2d");

                if (!context) {
                  throw new Error("Unable to create PDF page image");
                }

                context.fillStyle = "#ffffff";
                context.fillRect(0, 0, slice.width, slice.height);
                context.drawImage(
                  canvas,
                  0,
                  offset,
                  canvas.width,
                  sliceHeight,
                  0,
                  0,
                  slice.width,
                  slice.height,
                );
                pdf.addImage(
                  slice.toDataURL("image/jpeg", htmlImageQuality),
                  "JPEG",
                  0,
                  0,
                  pageWidth,
                  sliceHeight / pixelsPerMm,
                  undefined,
                  "FAST",
                );
                offset += sliceHeight;
              }

              continue;
            }

            const imgData = canvas.toDataURL("image/jpeg", htmlImageQuality);
            const imgRatio = canvas.height / canvas.width;
            let imgWidth = pageWidth;
            let imgHeight = imgWidth * imgRatio;

            if (imgHeight > pageHeight) {
              imgHeight = pageHeight;
              imgWidth = imgHeight / imgRatio;
            }

            const x = (pageWidth - imgWidth) / 2;
            const y = 0;

            pdf.addImage(
              imgData,
              "JPEG",
              x,
              y,
              imgWidth,
              imgHeight,
              undefined,
              "MEDIUM",
            );
          }
        });

        return pdf;
      } catch (error) {
        console.error("HTML to PDF conversion failed:", error);
        return generateTablePdf();
      } finally {
        setIsLoading(false);
      }
    };

    const generateTablePdf = (): jsPDF => {
      const doc = new jsPDF();
      doc.setFont("Yekan");

      if (Array.isArray(resultContent) && Array.isArray(resultContent[0])) {
        autoTable(doc, {
          head: [
            [
              {
                content: `نتیجه استعلام ${tableTitle} سامانه استعلام نوین`,
                colSpan: 2,
                styles: { halign: "center" },
              },
            ],
          ],
          body: resultContent as string[][],
          styles: {
            font: "Yekan",
            fontSize: 12,
            halign: "right",
          },
          headStyles: {
            font: "Yekan",
            fillColor: "#5a4bd2",
            textColor: 255,
            fontStyle: "normal",
            halign: "center",
            fontSize: 14,
          },
          margin: { top: 20 },
          theme: "grid",
        });
      } else if (
        Array.isArray(resultContent) &&
        resultContent.length > 0 &&
        typeof resultContent[0] === "object" &&
        !Array.isArray(resultContent[0])
      ) {
        const tableBody = (
          resultContent as (IBaseLabelValue | IInquiryDetailsResultValues)[]
        ).map((item) => [
          item.label,
          typeof item.value === "string"
            ? item.value
            : reactNodeToString(item.value),
        ]);
        if (inputData) {
          tableBody.unshift([inputData.label, inputData.value]);
        }
        autoTable(doc, {
          head: [
            [
              {
                content: `نتیجه استعلام ${tableTitle} سامانه استعلام نوین`,
                colSpan: 2,
                styles: { halign: "center" },
              },
            ],
          ],
          body: tableBody,
          styles: {
            font: "Yekan",
            fontSize: 12,
            halign: "right",
          },
          headStyles: {
            font: "Yekan",
            fillColor: "#5a4bd2",
            textColor: 255,
            fontStyle: "normal",
            halign: "center",
            fontSize: 14,
          },
          margin: { top: 20 },
          theme: "grid",
        });
      } else {
        const pageWidth = doc.internal.pageSize.getWidth();
        doc.setFont("Yekan");
        doc.setFontSize(16);
        doc.text(`نتیجه استعلام ${tableTitle}`, pageWidth / 2, 20, {
          align: "center",
        });
        doc.setFontSize(12);
        const maxWidth = pageWidth - 20;
        const lines = doc.splitTextToSize(resultContent as string, maxWidth);
        const startY = 35;
        const lineHeight = 10;
        lines.forEach((line: string, idx: number) => {
          doc.text(line, pageWidth / 2, startY + idx * lineHeight, {
            align: "center",
          });
        });
      }

      return doc;
    };

    const renderPdfTemplate = () => {
      if (customHtmlContent) {
        return customHtmlContent;
      }
      return (
        <div style={{ backgroundColor: "white", padding: "2rem" }}>
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <h1
              style={{
                fontSize: "1.5rem",
                fontWeight: "bold",
                color: "#1f2937",
                marginBottom: "0.5rem",
              }}
            >
              نتیجه استعلام {tableTitle}
            </h1>
            <p style={{ color: "#6b7280" }}>سامانه استعلام نوین</p>
          </div>

          {inputData && (
            <div
              style={{
                marginBottom: "1.5rem",
                padding: "1rem",
                backgroundColor: "#f9fafb",
                borderRadius: "0.5rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ fontWeight: "500" }}>{inputData.label}:</span>
                <span style={{ color: "#5a4bd2" }}>{inputData.value}</span>
              </div>
            </div>
          )}

          {Array.isArray(resultContent) &&
            resultContent.length > 0 &&
            typeof resultContent[0] === "object" &&
            !Array.isArray(resultContent[0]) && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                }}
              >
                {(resultContent as IBaseLabelValue[]).map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0.75rem",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <span style={{ fontWeight: "500", color: "#374151" }}>
                      {item.label}
                    </span>
                    <span style={{ color: "#5a4bd2" }}>{item.value}</span>
                  </div>
                ))}
              </div>
            )}

          <div
            style={{
              marginTop: "2rem",
              textAlign: "center",
              fontSize: "0.875rem",
              color: "#6b7280",
            }}
          >
            <p>تاریخ استعلام: {new Date().toLocaleDateString("fa-IR")}</p>
          </div>
        </div>
      );
    };

    return (
      <>
        {useHtmlToPdf && !htmlContentRef && (
          <div
            ref={defaultHtmlRef}
            className="fixed top-[-9999px] left-[-9999px] w-[800px]"
            style={{ fontFamily: "IRANYekan" }}
          >
            {renderPdfTemplate()}
          </div>
        )}

        <div
          id={id}
          onClick={handleDownloadPDF}
          className={cn(
            "border-primary-light flex w-max cursor-pointer items-center gap-1 rounded-lg border px-2 py-1 shadow-none hover:bg-gray-100 hover:shadow-none",
            className,
          )}
          title="دانلود pdf"
        >
          {showIcon && <PdfIcon size={25} />}
          <span className={cn("text-sm", labelClass)}>{title}</span>
        </div>
        {/*{isLoading && (*/}
        {/*  <div className="flex-center fixed inset-0 z-[1000] flex bg-gray-900/60">*/}
        {/*    <div className="flex-column flex-center flex w-60 rounded-xl bg-white p-3 sm:w-80 sm:p-4">*/}
        {/*      <Logo className="mb-3" src={MainLogo} width={60} height={60} />*/}
        {/*      <h2 className="text-center text-sm font-semibold sm:text-base">*/}
        {/*        درحال آماده سازی فایل برای شما*/}
        {/*        <br />*/}
        {/*        لطفا شکیبا باشید*/}
        {/*      </h2>*/}
        {/*      <div className="mt-4 size-4 sm:size-5">*/}
        {/*        <Loader color="bg-primary-main" />*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*)}*/}
        {isLoading && (
          <div className="flex-center fixed inset-0 z-[1000] flex bg-gray-900/60">
            <div className="flex-column flex-center flex w-60 rounded-xl bg-white p-3 sm:w-80 sm:p-4">
              <Logo className="mb-3" src={MainLogo} width={60} height={60} />
              <h2 className="text-center text-sm font-semibold sm:text-base">
                درحال آماده سازی فایل برای شما
                <br />
                لطفا شکیبا باشید
              </h2>
              <div className="mt-4 size-4 sm:size-5">
                <Loader color="bg-primary-main" />
              </div>
            </div>
          </div>
        )}
      </>
    );
  },
);

DownloadPdf.displayName = "DownloadPdf";

export default DownloadPdf;
